// Axios calls for the warehouse module.
// Points at eng-backend (port 5000) where /api/v1/warehouse is mounted.

import axios from 'axios';
import { API } from '../../config/api';

const http = axios.create({
  baseURL: API.BACKEND,
  timeout: 10000,
});

// Attach the auth token on every request so protect middleware passes.
http.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers['x-auth-token'] = token;
  }
  return config;
});

// Buildings ------------------------------------------------------------
export async function fetchBuildings() {
  const res = await http.get('/api/v1/warehouse/buildings');
  return res.data.data;
}

export async function fetchBuilding(buildingId) {
  const res = await http.get(`/api/v1/warehouse/buildings/${buildingId}`);
  return res.data.data;
}

export async function fetchOccupancy(buildingId) {
  const res = await http.get(`/api/v1/warehouse/buildings/${buildingId}/occupancy`);
  return res.data.data; // { building, occupancy }
}

// Slots ----------------------------------------------------------------
export async function fetchSlots(params = {}) {
  const res = await http.get('/api/v1/warehouse/slots', { params });
  return res.data.data;
}

export async function fetchSlot(code) {
  const res = await http.get(`/api/v1/warehouse/slots/${code}`);
  return res.data.data;
}

// Items ----------------------------------------------------------------
export async function fetchItems(params = {}) {
  const res = await http.get('/api/v1/warehouse/items', { params });
  return res.data.data;
}

export async function fetchItem(serial) {
  const res = await http.get(`/api/v1/warehouse/items/${serial}`);
  return res.data.data; // { item, movements }
}

export async function fetchSuggestedSlots(serial, buildingId) {
  const res = await http.get(`/api/v1/warehouse/items/${serial}/suggest-slots`, {
    params: buildingId ? { building: buildingId } : {},
  });
  return res.data.data;
}

export async function putAwayItem(serial, slotCode) {
  const res = await http.post(`/api/v1/warehouse/items/${serial}/put-away`, { slotCode });
  return res.data.data;
}

export async function placeItem(serial, toSlotCode, force = false) {
  const res = await http.post(`/api/v1/warehouse/items/${serial}/place`, {
    toSlotCode,
    force,
  });
  return res.data;
}

export async function moveItem(serial, toSlotCode) {
  const res = await http.post(`/api/v1/warehouse/items/${serial}/move`, { toSlotCode });
  return res.data.data;
}

export async function reserveItem(serial, orderId) {
  const res = await http.patch(`/api/v1/warehouse/items/${serial}/reserve`, { orderId });
  return res.data.data;
}

export async function unreserveItem(serial) {
  const res = await http.patch(`/api/v1/warehouse/items/${serial}/unreserve`);
  return res.data.data;
}

export async function scrapItem(serial, reason) {
  const res = await http.post(`/api/v1/warehouse/items/${serial}/scrap`, { reason });
  return res.data.data;
}

// PATCH a slot — used for block / unblock and (later) editing allowedTypes.
// Pass { status: 'blocked' } or { status: 'empty' } to toggle.
export async function patchSlot(code, updates) {
  const res = await http.patch(`/api/v1/warehouse/slots/${code}`, updates);
  return res.data.data;
}

// Trips ----------------------------------------------------------------
export async function fetchTrips(params = {}) {
  const res = await http.get('/api/v1/warehouse/trips', { params });
  return res.data.data;
}

export async function fetchTrip(code) {
  const res = await http.get(`/api/v1/warehouse/trips/${code}`);
  return res.data.data;
}

export async function createTrip(body) {
  const res = await http.post('/api/v1/warehouse/trips', body);
  return res.data.data;
}

export async function loadItemOnTrip(code, serial) {
  const res = await http.post(`/api/v1/warehouse/trips/${code}/load`, { serial });
  return res.data.data;
}

export async function unloadItemFromTrip(code, serial) {
  const res = await http.post(`/api/v1/warehouse/trips/${code}/unload`, { serial });
  return res.data.data;
}

export async function departTrip(code) {
  const res = await http.post(`/api/v1/warehouse/trips/${code}/depart`);
  return res.data.data;
}

export async function fetchPackingList(code) {
  const res = await http.get(`/api/v1/warehouse/trips/${code}/packing-list`);
  return res.data.data;
}

export async function fetchPickRoute(code) {
  const res = await http.get(`/api/v1/warehouse/trips/${code}/pick-route`);
  return res.data.data;
}

// Movements ------------------------------------------------------------
export async function fetchMovements(params = {}) {
  const res = await http.get('/api/v1/warehouse/movements', { params });
  return res.data.data;
}

// KPIs -----------------------------------------------------------------
export async function fetchKpis(params = {}) {
  const res = await http.get('/api/v1/warehouse/kpis', { params });
  return res.data.data;
}
